import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import {
    SITE_SECTION_DESCRIPTION_MAX_LENGTH,
    SITE_SECTION_DESCRIPTION_MIN_LENGTH,
    SITE_SECTION_PATH_MAX_LENGTH,
    SITE_SECTION_PATH_MIN_LENGTH,
    SITE_SECTION_TITLE_MAX_LENGTH,
    SITE_SECTION_TITLE_MIN_LENGTH,
} from '@gitbook/api-client';
import {
    Description,
    DialogRoot,
    FieldError,
    Fieldset,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { SpaceCreationDialog } from '../../Space';
import { PathOutput, PathPrefix } from './PathOutput';
import { SpacePickerField } from './SpacePickerField';
import { StickyFormControls } from './StickyFormControls';
import { TitleAndIconField } from './TitleAndIconField';
import { useAvailableSiteSpaces } from './useAvailableSiteSpaces';

export interface EditSiteSectionFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSection: api.SiteSection;
    siteStructure: api.SiteStructure;
    orgAllContent: api.OrganizationAllContent;
    disabled?: boolean;
}

type EditSectionFormData = {
    title: string;
    icon: string | null;
    description: string;
    path: string;
    spaceId: string | null;
};

function getDefaultValues(siteSection: api.SiteSection): EditSectionFormData {
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;
    return {
        title: siteSection.title,
        icon: siteSection.icon ?? null,
        description: siteSection.description ?? '',
        path: siteSection.path,
        spaceId: singleSiteSpace?.space.id ?? null,
    };
}

export function EditSiteSectionForm(props: EditSiteSectionFormProps) {
    const { organization, site, siteSection, siteStructure, orgAllContent, disabled } = props;
    const spaces = useAvailableSiteSpaces({ siteStructure, orgAllContent });
    const createSpaceDialogTrigger = useDialogTriggerState();
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;

    const form = useForm<EditSectionFormData>({
        defaultValues: getDefaultValues(siteSection),
    });
    const { defaultValues } = form.formState;
    const onSubmit: SubmitHandler<EditSectionFormData> = async (data) => {
        await toast.catch(async () => {
            if (singleSiteSpace && data.spaceId && data.spaceId !== singleSiteSpace.space.id) {
                await apiClient.siteSpaces.addSpaceToSite(organization.id, site.id, {
                    spaceId: data.spaceId,
                    sectionId: siteSection.id,
                });
                await apiClient.siteSpaces.deleteSiteSpaceById(
                    organization.id,
                    site.id,
                    singleSiteSpace.id
                );
            }

            const { data: newSiteSection } = await apiClient.siteSections.updateSiteSectionById(
                organization.id,
                site.id,
                siteSection.id,
                {
                    title: data.title,
                    icon: data.icon,
                    description: data.description,
                    path: data.path,
                }
            );
            form.reset(getDefaultValues(newSiteSection));
            toast.success('Section updated');
        }, 'Error while saving site section');
    };

    return (
        <>
            <DialogRoot {...createSpaceDialogTrigger.rootProps}>
                <SpaceCreationDialog organizationId={organization.id} />
            </DialogRoot>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <Fieldset className="p-4" disabled={disabled || form.formState.isSubmitting}>
                    {defaultValues?.spaceId ? (
                        <SpacePickerField
                            form={form}
                            name="spaceId"
                            spaces={[
                                ...siteSection.siteSpaces.map((siteSpace) => siteSpace.space),
                                ...spaces,
                            ]}
                            description="Select which space’s content will appear in this site section."
                            createSpaceDialogTrigger={createSpaceDialogTrigger}
                        />
                    ) : null}
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        autoFocus
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            This title and icon will appear in your site’s navigation.
                        </Description>
                    </TitleAndIconField>
                    <TextField
                        form={form}
                        name="description"
                        minLength={SITE_SECTION_DESCRIPTION_MIN_LENGTH}
                        maxLength={SITE_SECTION_DESCRIPTION_MAX_LENGTH}
                    >
                        <Label>Description</Label>
                        <Description>
                            This description will be displayed below the title in a section group to
                            give more context.
                        </Description>
                        <TextInput />
                        <FieldError />
                    </TextField>
                    <TextField
                        form={form}
                        name="path"
                        required
                        minLength={SITE_SECTION_PATH_MIN_LENGTH}
                        maxLength={SITE_SECTION_PATH_MAX_LENGTH}
                    >
                        <Label>Slug</Label>
                        <Description>Set the URL segment for your site section.</Description>
                        <TextInput
                            prefix={<PathPrefix item={siteSection} />}
                            className="@container"
                        />
                        <PathOutput item={siteSection} />
                        <FieldError />
                    </TextField>
                </Fieldset>
                <StickyFormControls>
                    <Submit label="Save" disabled={disabled} />
                </StickyFormControls>
            </Form>
        </>
    );
}
